import React, { useState, useRef, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Menu, X } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/artists", label: "Artists" },
  { to: "/explore", label: "Explore" },
  // { to: "/flash", label: "Flash" },
  // { to: "/community", label: "Community" },
  { to: "/blog", label: "Blog" },
];

export default function NavBar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);



  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <header className="border-b-[0.5px] border-black/10 bg-white sticky top-0 z-40" data-testid="navbar">
      <div className="container-x flex items-center justify-between h-16">
        <Link to="/" className="logo text-2xl text-black" data-testid="nav-logo">Inkstead</Link>
        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                "text-sm font-medium text-black/80 hover:text-black transition-colors " +
                (isActive ? "text-black underline underline-offset-4" : "")
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <div className="relative" ref={profileRef}>
              {/* Profile Button */}
              <button
                onClick={() => setProfileOpen((prev) => !prev)}
                className="flex items-center gap-2 rounded-full px-2 py-1.5 hover:bg-gray-100 transition-colors"
                aria-label="Open profile menu"
                aria-expanded={profileOpen}
                data-testid="nav-profile"
              >
                <div className="w-9 h-9 rounded-full bg-[#f3efe8] border border-[#ded7ca] flex items-center justify-center">
                  {user?.avatar_url ? (
                    <img
                      src={user.avatar_url}
                      alt="User avatar"
                      className="w-5 h-5 rounded-full object-cover"
                    />
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      className="w-5 h-5 text-gray-700"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15.75 6.75a3.75 3.75 0 1 1-7.5 0M4.5 20.25a7.5 7.5 0 0 1 15 0"
                      />
                    </svg>
                  )}

                </div>

                <span className="text-sm font-medium text-gray-700">
                  Welcome, {user?.name}
                </span>

                {/* Chevron */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className={`w-4 h-4 text-gray-400 transition-transform ${profileOpen ? "rotate-180" : ""
                    }`}
                >
                  <path
                    fillRule="evenodd"
                    d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              {/* Popover */}
              {profileOpen && (
                <div className="absolute right-0 top-full mt-2 w-56 rounded-xl border border-gray-200 bg-white shadow-lg z-50 overflow-hidden">
                  {/* User info */}
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-semibold text-gray-800">
                      {user?.name}
                    </p>
                    {user.email && (
                      <p className="text-xs text-gray-500 truncate mt-0.5">
                        {user.email}
                      </p>
                    )}
                  </div>

                  {/* Dashboard */}
                  <Link
                    to="/dashboard"
                    onClick={() => setProfileOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    data-testid="popover-dashboard"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      className="w-5 h-5 text-gray-500"
                    >
                      <rect
                        x="3"
                        y="3"
                        width="7"
                        height="7"
                        rx="1"
                      />
                      <rect
                        x="14"
                        y="3"
                        width="7"
                        height="7"
                        rx="1"
                      />
                      <rect
                        x="3"
                        y="14"
                        width="7"
                        height="7"
                        rx="1"
                      />
                      <rect
                        x="14"
                        y="14"
                        width="7"
                        height="7"
                        rx="1"
                      />
                    </svg>

                    <span>Dashboard</span>
                  </Link>

                  {/* Sign out */}
                  <button
                    onClick={async () => {
                      setProfileOpen(false);
                      await logout();
                      navigate("/");
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors border-t border-gray-100"
                    data-testid="popover-logout"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      className="w-5 h-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6A2.25 2.25 0 005.25 5.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M18 15l3-3m0 0l-3-3m3 3H9"
                      />
                    </svg>

                    <span>Sign out</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Link
                to="/sign-in"
                className="btn-outline"
                data-testid="nav-signin"
              >
                Sign in
              </Link>

              <Link
                to="/sign-up"
                className="btn-filled"
                data-testid="nav-signup"
              >
                Join Inkstead
              </Link>
            </>
          )}
        </div>

        <div className="md:hidden p-2 flex items-center">

          {user ? <div
            className="flex items-center gap-2 rounded-full px-2 py-1.5 my-2"
          >
            <div className="w-9 h-9 rounded-full bg-[#f3efe8] border border-[#ded7ca] flex items-center justify-center">
              {user?.avatar_url ? (
                <img
                  src={user.avatar_url}
                  alt="User avatar"
                  className="w-5 h-5 rounded-full object-cover"
                />
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  className="w-5 h-5 text-gray-700"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.75 6.75a3.75 3.75 0 1 1-7.5 0M4.5 20.25a7.5 7.5 0 0 1 15 0"
                  />
                </svg>
              )}

            </div>

            <span className="text-sm font-medium text-gray-700">
              Welcome, {user?.name}
            </span>
          </div> : null }


          <button className="md:hidden p-2" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Toggle menu">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>

        </div>
      </div>
      {open && (
        <div className="md:hidden border-t-[0.5px] border-black/10 bg-white" data-testid="mobile-menu">




          <div className="container-x flex flex-col py-4 gap-3">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)} className="text-base py-1" data-testid={`mobile-nav-${l.label.toLowerCase()}`}>{l.label}</NavLink>
            ))}
            {user ? (
              <>
                <Link to="/dashboard" className="btn-outline mt-2" onClick={() => setOpen(false)}>Dashboard</Link>
                <button className="btn-filled" onClick={async () => { await logout(); setOpen(false); navigate("/"); }}>Sign out</button>
              </>
            ) : (
              <>
                <Link to="/sign-in" className="btn-outline mt-2" onClick={() => setOpen(false)}>Sign in</Link>
                <Link to="/sign-up" className="btn-filled" onClick={() => setOpen(false)}>Join Inkstead</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
